import React from 'react'
import { Modal } from '../components/common/Modal'

interface ParentsGuideModalProps {
  isOpen: boolean
  onClose: () => void
}

export function ParentsGuideModal({ isOpen, onClose }: ParentsGuideModalProps): React.ReactElement {
  const guides = [
    {
      title: 'Getting Started',
      icon: '🚀',
      items: [
        'Create a parent account with email and password',
        'Add your first child (you can add multiple)',
        'Customize parental controls and time limits',
        'Review the dashboard to track progress',
      ]
    },
    {
      title: 'Monitoring Progress',
      icon: '📊',
      items: [
        'Check the Dashboard to see daily stats',
        'Review learning milestones and achievements',
        'Monitor screen time usage',
        'See coins earned and items purchased',
      ]
    },
    {
      title: 'Setting Limits',
      icon: '⏰',
      items: [
        'Set daily playtime limits (30 min to 2 hours)',
        'Enable session breaks (recommended every 30 min)',
        'Schedule offline hours (bedtime, mealtimes)',
        'Enable age-appropriate content filters',
      ]
    },
    {
      title: 'In-App Purchases',
      icon: '💳',
      items: [
        'Require parental approval for purchases',
        'Set spending limits on coins',
        'Review premium subscription options',
        'Manage payment methods in Account settings',
      ]
    },
    {
      title: 'Safety Tips',
      icon: '🛡️',
      items: [
        'Review child profile names (keep them non-identifying)',
        'Encourage healthy play habits',
        'Talk to your child about their achievements',
        'Report inappropriate content immediately',
      ]
    },
    {
      title: 'Troubleshooting',
      icon: '🔧',
      items: [
        'Forgot password? Use the reset link on login page',
        'App crashing? Try clearing cache and restarting',
        'Child locked out? Contact support for account recovery',
        'Billing issues? Check Account > Billing & Subscriptions',
      ]
    },
  ]

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Parents Guide" size="lg">
      <div className="space-y-6">
        <div className="text-center">
          <div className="text-6xl mb-3">👨‍👩‍👧‍👦</div>
          <h3 className="text-2xl font-black text-purple-700 mb-2">Welcome, Parents!</h3>
          <p className="text-gray-700 font-bold">
            Here&apos;s a quick guide to get the most out of BabyKingdom
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {guides.map((guide, idx) => (
            <div key={idx} className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border-4 border-purple-200">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-4xl">{guide.icon}</span>
                <h4 className="text-xl font-black text-purple-700">{guide.title}</h4>
              </div>
              <ul className="space-y-2">
                {guide.items.map((item, iIdx) => (
                  <li key={iIdx} className="flex gap-2 items-start">
                    <span className="font-black text-pink-500 mt-1">▪</span>
                    <span className="text-gray-700 font-bold text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="space-y-4 pt-4 border-t-4 border-purple-200">
          <div className="p-6 bg-blue-50 rounded-2xl border-4 border-blue-200">
            <h4 className="font-black text-blue-700 mb-3 text-lg">❓ Quick Tips</h4>
            <ul className="space-y-2">
              <li className="text-gray-700 font-bold">
                ✨ <strong>Pro Tip:</strong> Play with your child! BabyKingdom is more fun when you&apos;re involved.
              </li>
              <li className="text-gray-700 font-bold">
                ✨ <strong>Learning Power:</strong> Coins earned increase when your child completes educational games.
              </li>
              <li className="text-gray-700 font-bold">
                ✨ <strong>Balance:</strong> We recommend 30-60 minutes of daily play for optimal learning and retention.
              </li>
              <li className="text-gray-700 font-bold">
                ✨ <strong>Celebrate:</strong> When your child earns an achievement, celebrate with them!
              </li>
            </ul>
          </div>

          <div className="p-6 bg-green-50 rounded-2xl border-4 border-green-200">
            <h4 className="font-black text-green-700 mb-3 text-lg">🎯 Recommended Settings</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="font-bold text-gray-700">Daily Time Limit:</span>
                <span className="font-black text-green-700">30-60 minutes</span>
              </div>
              <div className="flex justify-between">
                <span className="font-bold text-gray-700">Session Break:</span>
                <span className="font-black text-green-700">Every 30 minutes</span>
              </div>
              <div className="flex justify-between">
                <span className="font-bold text-gray-700">Content Filter:</span>
                <span className="font-black text-green-700">Age-Appropriate</span>
              </div>
              <div className="flex justify-between">
                <span className="font-bold text-gray-700">Purchase Approval:</span>
                <span className="font-black text-green-700">Enabled</span>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center p-6 bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl border-4 border-purple-300">
          <p className="font-bold text-gray-800 mb-2">Need more help?</p>
          <p className="text-gray-700 font-semibold">
            Check out our full Help section or contact support at support@babykingdom.com
          </p>
        </div>
      </div>
    </Modal>
  )
}
